import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';

const VerifyEmailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
      // Redirect to pending approval page
      navigate('/pending-approval');
    } catch (err) {
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new one.');
      } else {
        setError(err.message || 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      await resendSignUpCode({ username: email });
      setResendSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="card-dark p-8 text-center">
            <h2 className="text-2xl font-bold text-dark-100 mb-4">Email Required</h2>
            <p className="text-dark-400 mb-6">
              Please register first to verify your email.
            </p>
            <Link to="/register" className="btn-primary inline-block">
              Go to Registration
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-gradient">MyMDO</h1>
          </Link>
          <p className="mt-2 text-dark-400">Verify your email</p>
        </div>

        {/* Card */}
        <div className="card-dark p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-primary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <p className="text-dark-300">
              We sent a verification code to
            </p>
            <p className="text-dark-100 font-medium">{email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Alert */}
            {error && (
              <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
                <p className="text-error-400 text-sm">{error}</p>
              </div>
            )}

            {/* Success Alert */}
            {resendSuccess && (
              <div className="bg-success-500/10 border border-success-500/30 rounded-lg p-4">
                <p className="text-success-400 text-sm">New verification code sent!</p>
              </div>
            )}

            {/* Code Input */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-dark-300 mb-2">
                Verification code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="input-dark w-full text-center text-2xl tracking-widest"
                placeholder="000000"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || code.length !== 6}
              className="btn-primary w-full flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Verifying...
                </>
              ) : (
                'Verify email'
              )}
            </button>
          </form>

          {/* Resend Code */}
          <div className="mt-6 text-center">
            <p className="text-dark-400 text-sm">
              Didn&apos;t receive the code?{' '}
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
                className="link-primary"
              >
                {isResending ? 'Sending...' : 'Resend code'}
              </button>
            </p>
          </div>
        </div>

        {/* Back to login */}
        <p className="mt-8 text-center text-dark-500 text-sm">
          <Link to="/login" className="link-primary">
            ← Back to login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
